import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Loader2, Lock, BookOpen, FileText, ClipboardCheck, ChevronRight, Play, CheckCircle2, Sparkles, GraduationCap } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { PreviewBanner } from "@/components/PreviewBanner";
import { toast } from "sonner";

const STUDY = "https://images.unsplash.com/photo-1472162072942-cd5147eb3902?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGNoaWxkJTIwbGVhcm5pbmclMjBmcmllbmRseXxlbnwwfHx8fDE3ODQyMzc2Nzh8MA&ixlib=rb-4.1.0&q=85";
const STUDY_PREP = "https://images.unsplash.com/photo-1532619675605-1ede6c2ed2b0?auto=format&fit=crop&w=1600&q=80";
const GRADE_LABEL = (g) => (g === "K" ? "Kindergarten" : `Grade ${g}`);

export default function CourseDetail() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [unlocking, setUnlocking] = useState(false);

  const unlockPremium = async () => {
    if (!user) { navigate("/login"); return; }
    setUnlocking(true);
    try {
      const { data } = await api.post("/checkout/premium-course", { course_id: courseId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setUnlocking(false);
    }
  };

  const unlockBundle = async () => {
    if (!user) { navigate("/login"); return; }
    setUnlocking(true);
    try {
      const { data } = await api.post("/checkout/premium-subject", { subject_id: course.subject_bundle.subject_id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setUnlocking(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setProgress(null);
    api.get(`/courses/${courseId}`)
      .then((r) => {
        setCourse(r.data);
        if (r.data.access_granted) {
          api.get(`/progress/course/${courseId}`).then((p) => setProgress(p.data)).catch(() => {});
        }
      })
      .catch(() => setCourse(null))
      .finally(() => setLoading(false));
  }, [courseId]);

  if (loading) return <div className="kid-theme flex min-h-screen items-center justify-center"><Loader2 className="h-10 w-10 animate-spin text-primary" /></div>;
  if (!course) return <div className="kid-theme min-h-screen"><div className="mx-auto max-w-3xl px-5 py-24 text-center text-lg font-bold text-muted-foreground">Course not found. <Link to="/curriculum/browse" className="text-primary underline">Back to catalog</Link></div></div>;

  const locked = !course.access_granted;
  const bundle = course.subject_bundle; // {subject_id, subject_name, bundle_price} when active
  const isCollegePrep = course.subject_category === "college_prep";
  const heroImg = isCollegePrep ? STUDY_PREP : STUDY;
  const themeClass = isCollegePrep ? "collegeprep-theme" : "kid-theme";
  const totalLessons = (course.units || []).reduce((a, u) => a + (u.lessons?.length || 0), 0);

  const openLesson = (lessonId) => {
    if (!user) { navigate("/login"); return; }
    if (locked) { navigate("/pricing"); return; }
    navigate(`/lesson/${lessonId}`);
  };

  return (
    <div className={`${themeClass} min-h-screen`}>
      {/* Header */}
      <div className="relative overflow-hidden border-b-2 border-border">
        <img src={heroImg} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-background/85" />
        <div className="relative mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <Link to="/curriculum/browse" className="label-caps text-muted-foreground hover:text-primary">← Curriculum</Link>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <span className="label-caps px-2 py-1" style={{ backgroundColor: `${course.subject_color}22`, color: course.subject_color }}>{course.subject_name}</span>
            <span className="label-caps text-muted-foreground">{GRADE_LABEL(course.grade_level)}</span>
            {bundle && <span className="flex items-center gap-1 border border-primary/60 px-2 py-1 text-[0.6rem] font-bold uppercase tracking-widest text-primary"><GraduationCap className="h-3 w-3" /> {bundle.subject_name} Program</span>}
            {!bundle && course.is_premium && <span className="flex items-center gap-1 border border-primary/60 px-2 py-1 text-[0.6rem] font-bold uppercase tracking-widest text-primary"><Lock className="h-3 w-3" /> Premium Elective</span>}
          </div>
          <h1 className="mt-4 max-w-3xl font-head text-4xl font-black leading-tight sm:text-5xl">{course.title}</h1>
          <p className="mt-4 max-w-2xl text-muted-foreground">{course.description}</p>
          <div className="mt-6 flex flex-wrap gap-6 text-sm">
            <span className="flex items-center gap-2 text-foreground"><BookOpen className="h-4 w-4 text-primary" />{course.units?.length || 0} Units</span>
            <span className="flex items-center gap-2 text-foreground"><FileText className="h-4 w-4 text-primary" />{totalLessons} Lessons</span>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl gap-8 px-5 py-12 lg:grid-cols-3 lg:px-8">
        <div className="lg:col-span-3"><PreviewBanner /></div>
        {/* Syllabus */}
        <div className="lg:col-span-2">
          <h2 className="font-head text-2xl font-bold">Syllabus</h2>
          <div className="mt-6">
            <Accordion type="multiple" defaultValue={course.units?.slice(0, 1).map((u) => u.unit_id)} className="space-y-3">
              {(course.units || []).map((u, idx) => (
                <AccordionItem key={u.unit_id} value={u.unit_id} className="overflow-hidden rounded-2xl border-2 border-border bg-card px-4">
                  <AccordionTrigger className="hover:no-underline" data-testid={`unit-${u.unit_id}`}>
                    <div className="flex items-center gap-3 text-left">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 font-head text-base font-black text-primary">{idx + 1}</span>
                      <span className="font-head text-lg font-black">{u.title}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="divide-y divide-border border-t border-border">
                      {(u.lessons || []).map((l) => (
                        <button
                          key={l.lesson_id}
                          onClick={() => openLesson(l.lesson_id)}
                          data-testid={`lesson-row-${l.lesson_id}`}
                          className="flex w-full items-center justify-between gap-3 py-3 text-left font-semibold transition-colors hover:text-primary"
                        >
                          <span className="flex items-center gap-3 text-sm">
                            {locked ? <Lock className="h-4 w-4 shrink-0 text-muted-foreground" />
                              : progress?.lessons?.[l.lesson_id] === "completed" ? <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                              : <Play className="h-4 w-4 shrink-0 text-primary" />}
                            {l.title}
                          </span>
                          <span className="flex shrink-0 items-center gap-3 text-xs text-muted-foreground">
                            {l.assessment_count ? <span className="flex items-center gap-1"><ClipboardCheck className="h-3 w-3" />{l.assessment_count}</span> : null}
                            <ChevronRight className="h-4 w-4" />
                          </span>
                        </button>
                      ))}
                      {(!u.lessons || u.lessons.length === 0) && <p className="py-3 text-sm text-muted-foreground">Lessons coming soon.</p>}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="lg:sticky lg:top-24 lg:h-fit">
          <div className="rounded-3xl border-2 border-border bg-card p-6 shadow-sm">
            {locked ? (
              bundle ? (
                <>
                  <div className="flex items-center gap-2 text-primary"><GraduationCap className="h-5 w-5" /><span className="label-caps">{bundle.subject_name} Program</span></div>
                  <h3 className="mt-3 font-head text-2xl font-bold">Unlock the Entire Program</h3>
                  <p className="mt-2 text-4xl font-black text-foreground" data-testid="bundle-price">${Number(bundle.bundle_price).toFixed(2)}</p>
                  <p className="mt-1 text-sm text-muted-foreground">One-time payment · unlocks every course in <strong>{bundle.subject_name}</strong>, including this one and any added later.</p>
                  {course.base_access ? (
                    <Button onClick={unlockBundle} disabled={unlocking} className="mt-5 w-full rounded-full bg-primary py-6 text-base font-extrabold text-white tactile hover:bg-primary" data-testid="bundle-unlock-btn">
                      {unlocking ? <Loader2 className="h-5 w-5 animate-spin" /> : `Unlock Program for $${Number(bundle.bundle_price).toFixed(2)}`}
                    </Button>
                  ) : (
                    <>
                      <p className="mt-4 rounded-2xl bg-muted p-3 text-sm font-semibold text-muted-foreground">Programs require an active membership or a grade enrollment first.</p>
                      <Link to="/pricing" data-testid="bundle-base-access-btn"><Button className="mt-3 w-full rounded-full bg-primary py-6 text-base font-extrabold text-white tactile hover:bg-primary">Get Base Access</Button></Link>
                    </>
                  )}
                  {!user && <Link to="/login"><Button variant="outline" className="mt-3 w-full rounded-full border-2 py-6 font-extrabold">Log In</Button></Link>}
                </>
              ) : course.is_premium ? (
                <>
                  <div className="flex items-center gap-2 text-primary"><Sparkles className="h-5 w-5" /><span className="label-caps">Premium Elective</span></div>
                  <h3 className="mt-3 font-head text-2xl font-bold">Unlock This Elective</h3>
                  <p className="mt-2 text-4xl font-black text-foreground" data-testid="premium-price">${Number(course.premium_price).toFixed(2)}</p>
                  <p className="mt-1 text-sm text-muted-foreground">One-time payment · permanent access to every lesson in this course.</p>
                  {course.base_access ? (
                    <Button onClick={unlockPremium} disabled={unlocking} className="mt-5 w-full rounded-full bg-primary py-6 text-base font-extrabold text-white tactile hover:bg-primary" data-testid="premium-unlock-btn">
                      {unlocking ? <Loader2 className="h-5 w-5 animate-spin" /> : `Unlock for $${Number(course.premium_price).toFixed(2)}`}
                    </Button>
                  ) : (
                    <>
                      <p className="mt-4 rounded-2xl bg-muted p-3 text-sm font-semibold text-muted-foreground">Premium electives require an active membership or a {GRADE_LABEL(course.grade_level)} enrollment first.</p>
                      <Link to="/pricing" data-testid="premium-base-access-btn"><Button className="mt-3 w-full rounded-full bg-primary py-6 text-base font-extrabold text-white tactile hover:bg-primary">Get Base Access</Button></Link>
                    </>
                  )}
                  {!user && <Link to="/login"><Button variant="outline" className="mt-3 w-full rounded-full border-2 py-6 font-extrabold">Log In</Button></Link>}
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-primary"><Lock className="h-5 w-5" /><span className="label-caps">Locked</span></div>
                  <h3 className="mt-3 font-head text-2xl font-bold">Unlock This Course</h3>
                  <p className="mt-2 text-sm text-muted-foreground">Enroll in {GRADE_LABEL(course.grade_level)} or start a monthly membership to open every lesson.</p>
                  <Link to="/pricing" data-testid="course-unlock-btn">
                    <Button className="mt-5 w-full rounded-full bg-primary py-6 text-base font-extrabold text-white tactile hover:bg-primary">View Enrollment</Button>
                  </Link>
                  {!user && <Link to="/login"><Button variant="outline" className="mt-3 w-full rounded-full border-2 py-6 font-extrabold">Log In</Button></Link>}
                </>
              )
            ) : (
              <>
                <div className="flex items-center gap-2 text-secondary-foreground"><BookOpen className="h-5 w-5 text-primary" /><span className="label-caps text-primary">Unlocked</span></div>
                <h3 className="mt-3 font-head text-2xl font-bold">You're Enrolled</h3>
                {progress && progress.total > 0 && (
                  <div className="mt-4" data-testid="course-progress">
                    <div className="flex items-center justify-between text-sm">
                      <span className="label-caps text-muted-foreground">Progress</span>
                      <span className="font-mono text-primary">{progress.completed}/{progress.total}</span>
                    </div>
                    <Progress value={(progress.completed / progress.total) * 100} className="mt-2 h-4 rounded-full bg-muted" />
                  </div>
                )}
                <p className="mt-3 text-sm text-muted-foreground">Full access granted. Jump into a lesson or open the AI Tutor for help.</p>
                {course.units?.[0]?.lessons?.[0] && (
                  <Button onClick={() => openLesson(course.units[0].lessons[0].lesson_id)} className="mt-5 w-full rounded-full bg-[#22C55E] py-6 text-base font-extrabold text-white tactile hover:bg-[#22C55E]" data-testid="course-start-btn">Start Learning</Button>
                )}
                <Link to="/tutor"><Button variant="outline" className="mt-3 w-full rounded-full border-2 py-6 font-extrabold">Ask the AI Tutor</Button></Link>
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
